import { z } from "zod";
import { RentalType, GenderType, PersonType } from "@/shared/types/enums";

export const step1Schema = z.object({
  contractId: z.number().min(1, "Contrato é obrigatório"),
  contractDisplay: z.string(),
  sellerId: z.number().min(1, "Vendedor é obrigatório"),
});

export type Step1Data = z.infer<typeof step1Schema>;

export const step2Schema = z
  .object({
    rentalType: z.nativeEnum(RentalType),
    stockIds: z.array(z.number()),
    gownItems: z.array(
      z.object({
        productId: z.number(),
        quantity: z.number().min(1, "Quantidade mínima é 1"),
      }),
    ),
    hasShoes: z.boolean(),
    shoesPairQuantity: z.number().min(0),
    description: z.string().max(200, "Máximo 200 caracteres").optional(),
    tagIds: z.array(z.number()),
  })
  .refine(
    (data) => {
      if (
        data.rentalType === RentalType.SUIT &&
        data.hasShoes &&
        data.shoesPairQuantity < 1
      )
        return false;
      return true;
    },
    {
      message: "Informe a quantidade de pares de sapatos",
      path: ["shoesPairQuantity"],
    },
  );

export type Step2Data = z.infer<typeof step2Schema>;

export const step3Schema = z
  .object({
    totalValue: z.number().positive("Valor deve ser maior que zero"),
    expectedOutAt: z.string().min(1, "Data esperada de saída é obrigatória"),
    expectedInAt: z.string().min(1, "Data esperada de retorno é obrigatória"),
    isRetroactive: z.boolean(),
    outAt: z.string().optional(),
    inAt: z.string().optional(),
    payments: z.array(
      z.object({
        paidValue: z.number().positive("Valor do pagamento deve ser maior que zero"),
        paymentMethod: z.enum(["PIX", "CREDIT_CARD", "DEBIT_CARD", "CASH"]),
        installments: z.number().int().min(1).max(12).optional(),
      }),
    ),
    emitInvoice: z.boolean(),
    invoiceObservation: z
      .string()
      .max(2000, "Máximo 2000 caracteres")
      .optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.isRetroactive) return;
    if (!data.outAt) {
      ctx.addIssue({
        code: "custom",
        path: ["outAt"],
        message: "Data efetiva de saída é obrigatória",
      });
    }
    if (!data.inAt) {
      ctx.addIssue({
        code: "custom",
        path: ["inAt"],
        message: "Data efetiva de retorno é obrigatória",
      });
    }
  });

export type Step3Data = z.infer<typeof step3Schema>;

export const newClientSchema = z.object({
  name: z.string().min(2, "Nome deve ter pelo menos 2 caracteres"),
  document: z.string().min(1, "Documento é obrigatório"),
  personType: z.nativeEnum(PersonType).optional(),
  gender: z.nativeEnum(GenderType).nullable().optional(),
  phone: z.string().min(1, "Telefone é obrigatório"),
  email: z.string().email("Email inválido").optional().or(z.literal("")),
  street: z.string().min(1, "Rua é obrigatória"),
  number: z
    .string()
    .min(1, "Número é obrigatório")
    .max(10, "Número deve ter no máximo 10 caracteres"),
  complement: z.string().min(1, "Complemento é obrigatório"),
  neighborhood: z.string().min(1, "Bairro é obrigatório"),
  state: z.string().length(2, "UF deve ter 2 caracteres"),
  postalCode: z.string().min(1, "CEP é obrigatório"),
});

export type NewClientData = z.infer<typeof newClientSchema>;
